import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { request } from '../api/client'
import { useTenant } from '../api/tenant'
import type { Session } from '../api/types'
import { Failure } from '../components/Failure'

export interface Tile {
  id: string
  title: string
  filters: Record<string, string>
  group_by: string
  window_hours: number
  position: number
  created_by: string
  created_at: string
}

interface TileResult {
  from: string
  to: string
  took_ms?: number
  count?: number
  truncated?: boolean
  group_by?: string
  groups?: { value: string; count: number }[]
}

function search(tile: Tile): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(tile.filters)) {
    if (value) params.set(key, value)
  }
  // Always relative. A tile that could ask for an absolute range would show the same
  // numbers every morning while looking live.
  params.set('window_hours', String(tile.window_hours))
  if (tile.group_by) params.set('group_by', tile.group_by)
  return params.toString()
}

/** The hunt screen, opened on this tile's query. */
function huntLink(tile: Tile): string {
  const params = new URLSearchParams(tile.filters)
  if (tile.group_by) params.set('group_by', tile.group_by)
  return `/hunt?${params.toString()}`
}

/**
 * The dashboard: hunts a shift keeps looking at.
 *
 * Every tile is a query, re-asked when the page loads — never a stored number. And
 * every tile answers for itself: one tile whose query fails says so in its own box
 * while the rest of the wall keeps working, because a dashboard that goes blank when
 * one panel breaks gets replaced by a wiki page of saved links.
 *
 * A failed tile never renders a zero. That is the whole discipline: "nothing matched"
 * and "we could not ask" are different answers, and only one of them is good news.
 */
export function DashboardPage({ session }: { session: Session }) {
  const slugs = session.memberships.map((membership) => membership.tenant)
  const [tenant] = useTenant(slugs)
  const client = useQueryClient()

  const tiles = useQuery({
    queryKey: ['dashboard-tiles', tenant],
    queryFn: () =>
      request<Tile[]>(
        `/dashboard/tiles/${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ''}`,
      ),
  })

  const remove = useMutation({
    mutationFn: (id: string) =>
      request<void>(`/dashboard/tiles/${id}/`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['dashboard-tiles'] }),
  })

  if (tiles.isError) return <Failure error={tiles.error} onRetry={() => tiles.refetch()} />

  return (
    <section aria-labelledby="dashboard-heading">
      <h2 id="dashboard-heading">Dashboard</h2>

      {tiles.isSuccess && tiles.data.length === 0 && (
        <p className="empty">
          Nothing pinned yet. Run a search on the <Link to="/hunt">hunt screen</Link> and save it
          here — a tile is a question you want asked again, not a number somebody wrote down.
        </p>
      )}

      <div
        style={{
          display: 'grid',
          gap: 'var(--space-4)',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          alignItems: 'start',
        }}
      >
        {(tiles.data ?? []).map((tile) => (
          <TilePanel key={tile.id} tile={tile} onRemove={() => remove.mutate(tile.id)} />
        ))}
      </div>
    </section>
  )
}

function TilePanel({ tile, onRemove }: { tile: Tile; onRemove: () => void }) {
  const query = useQuery({
    queryKey: ['tile', tile.id],
    queryFn: () => request<TileResult>(`/events/?${search(tile)}`),
    retry: false,
    // Re-asked while somebody is watching it. The window is relative, so a refresh
    // genuinely moves it forward rather than re-fetching the same frozen range.
    refetchInterval: 60_000,
  })

  return (
    <section className="panel" aria-labelledby={`tile-${tile.id}`}>
      <header style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}>
        <h3 id={`tile-${tile.id}`} style={{ margin: 0, fontSize: 'var(--text-md)' }}>
          {tile.title}
        </h3>
        <button
          className="linklike"
          style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)' }}
          aria-label={`Remove the tile ${tile.title}`}
          onClick={onRemove}
        >
          remove
        </button>
      </header>
      <p className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 0 }}>
        {/* The window is on the tile, always. A count without one is not an answer. */}
        last {tile.window_hours} h
        {tile.group_by ? ` · by ${tile.group_by}` : ''} · saved by {tile.created_by}
      </p>

      {query.isError && (
        <p className="error" role="alert" style={{ marginBottom: 0 }}>
          This tile could not be answered, so its number is unknown — not zero.{' '}
          {(query.error as Error).message}
        </p>
      )}

      {query.isLoading && <div className="skeleton" />}

      {query.isSuccess && !tile.group_by && (
        <p style={{ fontSize: 'var(--text-xl)', margin: 0 }}>
          {(query.data.count ?? 0).toLocaleString()}
          <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
            {' '}
            event{query.data.count === 1 ? '' : 's'}
            {query.data.truncated ? ' — at the row limit, so this is a floor' : ''}
          </span>
        </p>
      )}

      {query.isSuccess && tile.group_by && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {(query.data.groups ?? []).length === 0 && (
            <li className="muted">Nothing matched in this window.</li>
          )}
          {(query.data.groups ?? []).slice(0, 8).map((group) => (
            <li
              key={group.value}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}
            >
              <span className="mono">{group.value}</span>
              <strong>{group.count.toLocaleString()}</strong>
            </li>
          ))}
          {query.data.truncated && (
            <li className="muted" style={{ fontSize: 'var(--text-xs)' }}>
              top values only — these are not all of them
            </li>
          )}
        </ul>
      )}

      {query.isSuccess && (
        <p className="muted" style={{ fontSize: 'var(--text-xs)', marginBottom: 0 }}>
          asked {new Date(query.data.to).toLocaleTimeString()} · {query.data.took_ms ?? '?'} ms ·{' '}
          <Link to={huntLink(tile)}>open in hunt</Link>
        </p>
      )}
    </section>
  )
}
